import { useMemo, useState } from 'react';
import { Download } from 'lucide-react';
import { UptimeDayDetail } from './UptimeDayDetail';
import { uptimePercent } from '@cortex/uptime-report';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { cn } from '@rudi/ui/lib/utils';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader, QueryState } from '../components/page';
import { useUptime, type CheckerView, type Environment, type TargetReport } from '../api/uptime';

const DAY_MS = 86_400_000;
const BAR_DAYS = 90;
const ENVIRONMENTS: { id: Environment; label: string }[] = [
  { id: 'production', label: 'Production' },
  { id: 'staging', label: 'Staging' },
  { id: 'dev', label: 'Dev' },
];
const control = 'rounded-3xl border bg-background px-3 py-2 text-sm';

const CHECKER_STATUS = {
  reporting: { label: 'Reporting', variant: 'default' },
  unreachable: { label: 'Not reporting', variant: 'destructive' },
  waiting: { label: 'Waiting for first report', variant: 'outline' },
} as const;

const TARGET_STATUS = {
  up: { label: 'Up', variant: 'default' },
  down: { label: 'Down', variant: 'destructive' },
  unknown: { label: 'Unknown', variant: 'outline' },
} as const;

export interface MergedTarget {
  id: string;
  name: string;
  environment: Environment;
  url: string;
  /** The target as each checker sees it, by checker ID. */
  byChecker: Record<string, TargetReport>;
  certificateExpiresAt: string | null;
}

/** Every target any checker reports, in the order the checkers list them. */
export function mergeTargets(checkers: CheckerView[]): MergedTarget[] {
  const merged = new Map<string, MergedTarget>();
  for (const checker of checkers) {
    for (const target of checker.report?.targets ?? []) {
      const entry = merged.get(target.id) ?? {
        id: target.id,
        name: target.name,
        environment: target.environment,
        url: target.url,
        byChecker: {},
        certificateExpiresAt: null,
      };
      entry.byChecker[checker.id] = target;
      if (
        target.certificateExpiresAt &&
        (!entry.certificateExpiresAt || target.certificateExpiresAt < entry.certificateExpiresAt)
      )
        entry.certificateExpiresAt = target.certificateExpiresAt;
      merged.set(target.id, entry);
    }
  }
  return [...merged.values()];
}

export function formatPercent(value: number | null): string {
  if (value === null) return 'No checks';
  if (value === 100) return '100 %';
  return `${value.toFixed(value >= 99 ? 2 : 1)} %`;
}

export function formatDuration(ms: number): string {
  const minutes = Math.max(1, Math.round(ms / 60_000));
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} h ${minutes % 60} min`;
  return `${Math.floor(hours / 24)} days ${hours % 24} h`;
}

function formatTime(value: string): string {
  return new Date(value).toLocaleString();
}

/** Checks and failures of one target in the dates matching `match`, per checker. */
export function counts(
  checkers: CheckerView[],
  target: string,
  match: (date: string) => boolean
): Record<string, { checks: number; failures: number }> {
  const result: Record<string, { checks: number; failures: number }> = {};
  for (const checker of checkers) {
    const total = { checks: 0, failures: 0 };
    for (const day of checker.report?.days ?? []) {
      if (day.target !== target || !match(day.date)) continue;
      total.checks += day.checks;
      total.failures += day.failures;
    }
    result[checker.id] = total;
  }
  return result;
}

/**
 * The lowest uptime any checker measured. A failure seen from only one
 * cluster may be that cluster's network, but it still counts: for an audit,
 * the conservative figure is the one to show.
 */
export function worstUptime(entries: { checks: number; failures: number }[]): number | null {
  const values = entries
    .map(entry => uptimePercent(entry.checks, entry.failures))
    .filter((value): value is number => value !== null);
  return values.length ? Math.min(...values) : null;
}

function utcDates(days: number, now: number): string[] {
  return Array.from({ length: days }, (_, index) =>
    new Date(now - (days - 1 - index) * DAY_MS).toISOString().slice(0, 10)
  );
}

function barColor(value: number | null): string {
  if (value === null) return 'bg-muted';
  if (value >= 99.9) return 'bg-primary';
  if (value >= 99) return 'bg-amber-500';
  return 'bg-destructive';
}

function DayBar({
  checkers,
  target,
  now,
}: {
  checkers: CheckerView[];
  target: string;
  now: number;
}) {
  const dates = utcDates(BAR_DAYS, now);
  return (
    <div className="flex h-6 items-stretch gap-px" aria-hidden="true">
      {dates.map(date => {
        const value = worstUptime(Object.values(counts(checkers, target, day => day === date)));
        return (
          <span
            key={date}
            title={`${date}: ${formatPercent(value)}`}
            className={cn('w-1 rounded-sm', barColor(value))}
          />
        );
      })}
    </div>
  );
}

function TargetCell({ target }: { target: TargetReport | undefined }) {
  if (!target) return <span className="text-muted-foreground">Not checked</span>;
  const status = TARGET_STATUS[target.status];
  return (
    <div className="space-y-1">
      <Badge variant={status.variant}>{status.label}</Badge>
      <div className="text-xs text-muted-foreground">
        {target.last?.ok && target.last.latencyMs !== null && `${target.last.latencyMs} ms`}
        {target.last && !target.last.ok && target.last.error}
        {target.status === 'down' && target.since && ` · since ${formatTime(target.since)}`}
      </div>
    </div>
  );
}

function EnvironmentSection({
  label,
  targets,
  checkers,
  now,
}: {
  label: string;
  targets: MergedTarget[];
  checkers: CheckerView[];
  now: number;
}) {
  if (!targets.length) return null;
  const since = new Date(now - (BAR_DAYS - 1) * DAY_MS).toISOString().slice(0, 10);
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold">{label}</h2>
      <div className="overflow-x-auto rounded-3xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              {checkers.map(checker => (
                <TableHead key={checker.id}>From {checker.label}</TableHead>
              ))}
              <TableHead>Last {BAR_DAYS} days</TableHead>
              <TableHead className="text-right">Uptime</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {targets.map(target => {
              const certificateDays = target.certificateExpiresAt
                ? Math.floor((Date.parse(target.certificateExpiresAt) - now) / DAY_MS)
                : null;
              return (
                <TableRow key={target.id}>
                  <TableCell>
                    <div className="font-medium">{target.name}</div>
                    <a
                      href={target.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-xs text-muted-foreground hover:underline"
                    >
                      {target.url}
                    </a>
                    {certificateDays !== null && certificateDays < 14 && (
                      <div className="text-xs text-destructive">
                        Certificate expires in {Math.max(0, certificateDays)} days
                      </div>
                    )}
                  </TableCell>
                  {checkers.map(checker => (
                    <TableCell key={checker.id}>
                      <TargetCell target={target.byChecker[checker.id]} />
                    </TableCell>
                  ))}
                  <TableCell>
                    <details className="group">
                      <summary
                        className="cursor-pointer text-sm"
                        aria-label={`Day details for ${target.name}`}
                      >
                        <span className="group-open:hidden">
                          <DayBar checkers={checkers} target={target.id} now={now} />
                        </span>
                        <span className="group-open:hidden">View day</span>
                        <span className="hidden group-open:inline">Hide day</span>
                      </summary>
                      <div className="hidden group-open:block">
                        <UptimeDayDetail checkers={checkers} target={target.id} now={now} />
                      </div>
                    </details>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {formatPercent(
                      worstUptime(Object.values(counts(checkers, target.id, d => d >= since)))
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function Incidents({
  checkers,
  targets,
  now,
}: {
  checkers: CheckerView[];
  targets: MergedTarget[];
  now: number;
}) {
  const names = new Map(targets.map(target => [target.id, target]));
  const rows = checkers
    .flatMap(checker => (checker.report?.incidents ?? []).map(incident => ({ checker, incident })))
    .sort((a, b) => b.incident.startedAt.localeCompare(a.incident.startedAt))
    .slice(0, 100);
  return (
    <section className="space-y-3">
      <h2 className="text-xl font-semibold">Incidents</h2>
      {!rows.length ? (
        <p className="text-sm text-muted-foreground">No incidents in the last {BAR_DAYS} days.</p>
      ) : (
        <div className="overflow-x-auto rounded-3xl border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Target</TableHead>
                <TableHead>Seen from</TableHead>
                <TableHead>Started</TableHead>
                <TableHead>Duration</TableHead>
                <TableHead>First error</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(({ checker, incident }) => {
                const target = names.get(incident.target);
                const end = incident.endedAt ? Date.parse(incident.endedAt) : now;
                return (
                  <TableRow key={`${checker.id}-${incident.target}-${incident.startedAt}`}>
                    <TableCell>
                      {target?.name ?? incident.target}
                      {target && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          {target.environment}
                        </span>
                      )}
                    </TableCell>
                    <TableCell>{checker.label}</TableCell>
                    <TableCell>{formatTime(incident.startedAt)}</TableCell>
                    <TableCell>
                      {incident.endedAt ? (
                        formatDuration(end - Date.parse(incident.startedAt))
                      ) : (
                        <Badge variant="destructive">
                          Ongoing, {formatDuration(end - Date.parse(incident.startedAt))}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="max-w-md text-sm text-muted-foreground">
                      {incident.error}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

/** Months (`YYYY-MM`) with any check, newest first. */
export function reportMonths(checkers: CheckerView[]): string[] {
  const months = new Set<string>();
  for (const checker of checkers)
    for (const day of checker.report?.days ?? []) months.add(day.date.slice(0, 7));
  return [...months].sort().reverse();
}

/** One row per production target and checker for the month, as CSV. */
export function monthCsv(checkers: CheckerView[], targets: MergedTarget[], month: string): string {
  const quote = (value: string) =>
    /[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value;
  const lines = [['month', 'target', 'url', 'checker', 'checks', 'failures', 'uptime_percent']];
  for (const target of targets.filter(t => t.environment === 'production')) {
    const byChecker = counts(checkers, target.id, date => date.startsWith(month));
    for (const checker of checkers) {
      const { checks, failures } = byChecker[checker.id];
      const uptime = uptimePercent(checks, failures);
      lines.push([
        month,
        target.name,
        target.url,
        checker.label,
        String(checks),
        String(failures),
        uptime === null ? '' : uptime.toFixed(3),
      ]);
    }
  }
  return lines.map(line => line.map(quote).join(',')).join('\n') + '\n';
}

function download(name: string, text: string) {
  const url = URL.createObjectURL(new Blob([text], { type: 'text/csv' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  link.click();
  URL.revokeObjectURL(url);
}

function MonthlyReport({
  checkers,
  targets,
}: {
  checkers: CheckerView[];
  targets: MergedTarget[];
}) {
  const months = useMemo(() => reportMonths(checkers), [checkers]);
  const [chosen, setChosen] = useState<string | null>(null);
  const month = chosen && months.includes(chosen) ? chosen : months[0];
  const production = targets.filter(target => target.environment === 'production');
  if (!month || !production.length) return null;
  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold">Monthly production uptime</h2>
          <p className="text-sm text-muted-foreground">
            Share of passed checks per UTC month. Cortex shows the last {BAR_DAYS} days; each
            checker keeps 13 months and mails the full report on the 1st.
          </p>
        </div>
        <div className="flex items-end gap-2">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">
            Month
            <select
              aria-label="Month"
              className={control}
              value={month}
              onChange={event => setChosen(event.target.value)}
            >
              {months.map(option => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </select>
          </label>
          <Button
            variant="outline"
            onClick={() => download(`uptime-${month}.csv`, monthCsv(checkers, targets, month))}
          >
            <Download className="mr-2 h-4 w-4" />
            CSV
          </Button>
        </div>
      </div>
      <div className="overflow-x-auto rounded-3xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Target</TableHead>
              {checkers.map(checker => (
                <TableHead key={checker.id} className="text-right">
                  From {checker.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {production.map(target => {
              const byChecker = counts(checkers, target.id, date => date.startsWith(month));
              return (
                <TableRow key={target.id}>
                  <TableCell className="font-medium">{target.name}</TableCell>
                  {checkers.map(checker => {
                    const { checks, failures } = byChecker[checker.id];
                    return (
                      <TableCell key={checker.id} className="text-right tabular-nums">
                        {formatPercent(uptimePercent(checks, failures))}
                        <div className="text-xs text-muted-foreground">
                          {checks} checks, {failures} failed
                        </div>
                      </TableCell>
                    );
                  })}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </section>
  );
}

function ago(timestamp: string, now: number): string {
  const minutes = Math.round((now - Date.parse(timestamp)) / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 90) return `${minutes} min ago`;
  return formatTime(timestamp);
}

export function UptimePage() {
  const { data, isLoading, error } = useUptime();
  const checkers = useMemo(() => data?.checkers ?? [], [data]);
  const targets = useMemo(() => mergeTargets(checkers), [checkers]);
  const now = Date.now();
  return (
    <div>
      <PageHeader
        title="Uptime"
        description="A checker on each cluster probes every public app on both clusters every 5 minutes and emails outages itself, so alerts do not depend on Cortex."
      />
      <QueryState isLoading={isLoading} error={error}>
        {!checkers.length ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            No uptime checkers are configured for this Cortex. List them in CORTEX_UPTIME_CHECKERS.
          </p>
        ) : (
          <div className="space-y-10">
            <div className="flex flex-wrap gap-4">
              {checkers.map(checker => {
                const status = CHECKER_STATUS[checker.status];
                return (
                  <div key={checker.id} className="flex items-center gap-2 text-sm">
                    <span className="font-medium">Checker {checker.label}</span>
                    <Badge variant={status.variant}>{status.label}</Badge>
                    {checker.receivedAt && (
                      <span className="text-muted-foreground">
                        last report {ago(checker.receivedAt, now)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
            {ENVIRONMENTS.map(environment => (
              <EnvironmentSection
                key={environment.id}
                label={environment.label}
                targets={targets.filter(target => target.environment === environment.id)}
                checkers={checkers}
                now={now}
              />
            ))}
            <MonthlyReport checkers={checkers} targets={targets} />
            <Incidents checkers={checkers} targets={targets} now={now} />
          </div>
        )}
      </QueryState>
    </div>
  );
}
